import { useEffect, useRef, useState } from "react";
import { SelectBox } from "devextreme-react";
import HtmlEditor, { Item, Toolbar as HTMLToolbar, MediaResizing } from "devextreme-react/html-editor";
import { CheckBox } from "devextreme-react";
import notify from "devextreme/ui/notify";
import { useTranslation } from "react-i18next";
import { getCurrentUser } from "../../../api/auth";
import {
    Ticket, TicketMessage, CannedResponse,
    sendMessage, uploadAttachment, getTicketMessages, getAttachmentUrl,
} from "../../../api/support";
import { onQueueEventName } from "../../../api/socket";
import { getInitials } from "./ticket-constants";

interface MessagesTabProps {
    ticket: Ticket;
    messages: TicketMessage[];
    cannedResponses: CannedResponse[];
    detailLoading: boolean;
    onMessagesUpdate: (msgs: TicketMessage[]) => void;
    onLoadingChange: (loading: boolean) => void;
    // Ic Notlar sekmesi: yalnizca dahili notlar gosterilir, gonderim daima dahilidir.
    internalOnly?: boolean;
}

export function MessagesTab({ ticket, messages, cannedResponses, detailLoading, onMessagesUpdate, onLoadingChange, internalOnly }: MessagesTabProps) {
    const { t } = useTranslation();
    const user = getCurrentUser();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [reply, setReply] = useState("");
    const [isInternalNote, setIsInternalNote] = useState(false);
    const [attachments, setAttachments] = useState<File[]>([]);

    // Ic Notlar modunda gonderim daima dahili; aksi halde checkbox'a bagli.
    const internalActive = internalOnly || isInternalNote;
    // Gosterilecek mesajlar: Ic Notlar modunda yalnizca dahili olanlar.
    const shown = internalOnly ? messages.filter(m => m.is_internal) : messages;

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // ── Live incoming messages for this ticket ─────────────────────────────
    useEffect(() => {
        const off = onQueueEventName<{
            type: string;
            ticketRef?: string;
            message?: TicketMessage;
        }>("queue.notification.push", async ({ payload }) => {
            if (!payload) return;
            if (payload.type !== "TICKET_MESSAGE_CREATED") return;
            if (payload.ticketRef !== ticket.ref) return;

            if (payload.message && payload.message.ref) {
                const incoming = payload.message;
                // Ayni mesaji iki kez ekleme (kendi gonderdigimizi ve socket echo'yu).
                if (messages.some(m => m.ref === incoming.ref)) return;
                onMessagesUpdate([...messages, incoming]);
            } else {
                // Payload'da mesaj yoksa yeniden cek.
                try {
                    const fresh = await getTicketMessages(ticket.ref);
                    onMessagesUpdate(fresh);
                } catch {}
            }
        });
        return off;
    }, [ticket.ref, messages, onMessagesUpdate]);

    const handleSendReply = async () => {
        if (!reply.trim()) return;
        onLoadingChange(true);
        try {
            const newMsg = await sendMessage(ticket.ref, reply, user?.id || "unknown", user?.name || t("supportTeam"), internalActive);
            for (const file of attachments) {
                await uploadAttachment(ticket.ref, newMsg.ref, file, user?.id || "unknown");
            }
            setReply(""); setAttachments([]); setIsInternalNote(false);
            onMessagesUpdate(await getTicketMessages(ticket.ref));
            notify(internalActive ? t("internalNoteAdded") : t("replySent"), "success", 2000);
        } catch {
            notify(t("messageSendError"), "error", 2000);
        } finally {
            onLoadingChange(false);
        }
    };

    const formatTime = (dateStr: string) =>
        new Date(dateStr).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

    const formatDateFull = (dateStr: string) =>
        new Date(dateStr).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });

    return (
        <div className="messages-tab-root">
            {/* ── Message list ── */}
            <div className="messages-tab-list">
                {shown.length === 0 ? (
                    <div className="messages-tab-empty">
                        <div className="messages-tab-empty-icon">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                            </svg>
                        </div>
                        <p className="text-sm font-medium mt-3">{internalOnly ? t("noInternalNotes") : t("noMessages")}</p>
                        <p className="text-xs mt-1 opacity-60">{internalOnly ? t("noInternalNotesDesc") : t("noMessagesDesc")}</p>
                    </div>
                ) : (
                    <div className="messages-tab-thread">
                        {shown.map((m, idx) => {
                            const isSupport = m.sender_type === "support";
                            const isInternal = m.is_internal;
                            const showDate = idx === 0 || new Date(shown[idx - 1].created_at).toDateString() !== new Date(m.created_at).toDateString();

                            return (
                                <div key={m.ref}>
                                    {/* Date divider */}
                                    {showDate && (
                                        <div className="messages-tab-date-divider">
                                            <span>{formatDateFull(m.created_at)}</span>
                                        </div>
                                    )}

                                    {/* Message bubble */}
                                    <div className={`messages-tab-row ${isSupport ? "is-support" : "is-customer"}`}>
                                        {/* Avatar */}
                                        <div className={`messages-tab-avatar ${isSupport ? "support" : "customer"}`}>
                                            {getInitials(m.sender_name || (isSupport ? "D" : "M"))}
                                        </div>

                                        {/* Content */}
                                        <div className="messages-tab-bubble-wrap">
                                            {/* Meta */}
                                            <div className="messages-tab-meta">
                                                <span className="messages-tab-sender">{m.sender_name || (isSupport ? t("support") : t("customer"))}</span>
                                                {isInternal && <span className="messages-tab-internal-badge">{t("internal")}</span>}
                                                <span className="messages-tab-time">{formatTime(m.created_at)}</span>
                                            </div>

                                            {/* Bubble */}
                                            <div className={`messages-tab-bubble ${isInternal ? "internal" : isSupport ? "support" : "customer"}`}>
                                                <div dangerouslySetInnerHTML={{ __html: m.content }} />

                                                {/* Attachments */}
                                                {m.attachments && m.attachments.length > 0 && (
                                                    <div className="messages-tab-attachments">
                                                        {m.attachments.map(att => (
                                                            <a key={att.ref} href="#"
                                                                onClick={e => { e.preventDefault(); const url = getAttachmentUrl(att.storage_path); if (url) window.open(url, "_blank"); }}
                                                                className="messages-tab-attachment">
                                                                <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                                </svg>
                                                                {att.file_name}
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            {/* ── Composer ── */}
            <div className={`messages-tab-composer ${internalActive ? "internal" : ""}`}>
                {/* Toolbar row */}
                <div className="messages-tab-composer-toolbar">
                    <div className="flex items-center gap-3">
                        {internalOnly ? (
                            <span className="messages-tab-internal-warn">{t("notVisibleToCustomer")}</span>
                        ) : (
                            <>
                                <CheckBox text={t("internalNote")} value={isInternalNote} onValueChanged={e => setIsInternalNote(e.value)} />
                                {isInternalNote && (
                                    <span className="messages-tab-internal-warn">{t("notVisibleToCustomer")}</span>
                                )}
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <SelectBox
                            dataSource={cannedResponses}
                            valueExpr="ref"
                            displayExpr="title"
                            placeholder={t("selectTemplate")}
                            width={180}
                            searchEnabled
                            showClearButton
                            onValueChanged={e => {
                                if (e.value) {
                                    const sel = cannedResponses.find(r => r.ref === e.value);
                                    if (sel) setReply(prev => prev + sel.content);
                                    e.component.option("value", null);
                                }
                            }}
                        />
                    </div>
                </div>

                {/* Input row */}
                <div className="messages-tab-composer-input-row">
                    <div className={`messages-tab-editor-wrap ${internalActive ? "internal" : ""}`}>
                        <HtmlEditor height={100} value={reply} onValueChanged={e => setReply(e.value)}>
                            <MediaResizing enabled />
                            <HTMLToolbar>
                                <Item name="bold" /><Item name="italic" /><Item name="underline" />
                                <Item name="separator" />
                                <Item name="orderedList" /><Item name="bulletList" />
                                <Item name="separator" /><Item name="link" />
                            </HTMLToolbar>
                        </HtmlEditor>
                    </div>

                    <div className="messages-tab-composer-actions">
                        <label className="messages-tab-attach-btn" title={t("attachFile")}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <input
                                type="file"
                                multiple
                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                                className="hidden"
                                onChange={e => setAttachments(Array.from(e.target.files || []))}
                            />
                        </label>
                        <button
                            onClick={handleSendReply}
                            disabled={detailLoading || !reply.trim()}
                            className={`messages-tab-send-btn ${internalActive ? "internal" : ""}`}
                            title={internalActive ? t("addInternalNote") : t("sendMessage")}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Attachment preview */}
                {attachments.length > 0 && (
                    <div className="messages-tab-attachment-preview">
                        {attachments.map((f, i) => (
                            <span key={i} className="messages-tab-attachment-chip">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                {f.name}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
